import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { trackByAwb, clearTracking } from "../../../../Redux/shipping-apis/slice";

interface TrackingDialogProps {
  open: boolean;
  onClose: () => void;
  /** AWB / tracking number. When provided, live tracking is fetched from the backend. */
  awb?: string;
  /** Optional display tracking id (falls back to awb). */
  trackingId?: string;
}

const formatDate = (ts?: string) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Order tracking dialog (ported from the OG TrackPackage modal design), rendered
 * as a React-controlled modal and driven by live tracking data when an AWB is
 * supplied (GET /shipments/by-awb/:awb/tracking via the shipping slice).
 */
const TrackingDialog = ({ open, onClose, awb, trackingId }: TrackingDialogProps) => {
  const dispatch = useAppDispatch();
  const { tracking, trackingLoading, trackingError } = useAppSelector(
    (state) => state.shipping,
  );

  useEffect(() => {
    if (open && awb) {
      dispatch(trackByAwb(awb));
    }
    if (!open) {
      dispatch(clearTracking());
    }
  }, [open, awb, dispatch]);

  if (!open) return null;

  const displayId = trackingId || awb || tracking?.awb || "—";
  const timeline = tracking?.timeline ?? [];

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={onClose}
      />
      <div
        className="modal fade show"
        style={{ display: "block", zIndex: 1055 }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Delivered by Salar
                <br />
                <span className="modal-title fw-normal text-sm">
                  Tracking ID : <b>{displayId}</b>
                </span>
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              {trackingLoading && (
                <p className="text-sm text-secondary">Loading tracking…</p>
              )}
              {!trackingLoading && trackingError && (
                <p className="text-sm text-danger">{trackingError}</p>
              )}

              {/* Live timeline */}
              {!trackingLoading && !trackingError && timeline.length > 0 && (
                <div
                  className="timeline timeline-one-side"
                  data-timeline-axis-style="dotted"
                >
                  {tracking?.status && (
                    <div className="mb-3">
                      <b className="text-success text-sm text-uppercase">
                        {tracking.status.replace(/_/g, " ")}
                      </b>
                      {tracking.estimatedDelivery && (
                        <div className="text-xs text-secondary">
                          Est. delivery: {formatDate(tracking.estimatedDelivery)}
                        </div>
                      )}
                    </div>
                  )}
                  {timeline.map((item, idx) => (
                    <div className="timeline-block mb-3" key={`${item.status}-${idx}`}>
                      <span className="timeline-step bg-success p-0"></span>
                      <div className="timeline-content pt-1">
                        <h6 className="text-dark text-sm font-weight-bold mb-1 mt-1">
                          {item.status}
                        </h6>
                        {item.location && (
                          <p className="text-xs text-dark mb-0">{item.location}</p>
                        )}
                        {item.timestamp && (
                          <b className="text-secondary fw-normal text-xs">
                            {formatDate(item.timestamp)}
                          </b>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty / no-AWB fallback */}
              {!trackingLoading && !trackingError && timeline.length === 0 && (
                <p className="text-sm text-secondary mb-0">
                  No tracking updates available yet.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TrackingDialog;
